import { apiClient } from "../API/axiosInstance";
import { logger } from "../utils/logger";

/**
 * Service class for Dashboard operations
 */
export class DashboardService {
  static async createDashboard(name, description, devices, organisation_id) {
    try {
      if (name === "") {
        return false;
      }

      let device_ids =
        devices && devices.length > 0 ? devices.map((device) => device.id) : [];

      let data = {
        name: name,
        description: description,
        devices: device_ids,
        organisation_id: organisation_id,
      };
      await apiClient.post("/dashboard/", data);
      return true;
    } catch (error) {
      logger.warn(error);
      return false;
    }
  }

  static async updateDashboard(layout, dashboard_id) {
    if (!dashboard_id) {
      return false;
    }

    if (!layout) {
      return false;
    }

    let data = {
      layout: layout,
    };

    try {
      let response = await apiClient.put(
        `/dashboard/${dashboard_id}/layout`,
        data
      );

      if (response && response.status >= 200 && response.status < 300) {
        return true;
      }

      return false;
    } catch (error) {
      logger.error("Dashboard update error:", error);
      return false;
    }
  }

  static async deleteDashboard(dashboardId) {
    if (!dashboardId) {
      logger.error("Dashboard delete error: No dashboard ID provided");
      return false;
    }

    try {
      const response = await apiClient.delete(`/dashboard/${dashboardId}`);
      return response && response.status === 200;
    } catch (error) {
      logger.error("Dashboard delete error:", error);
      return false;
    }
  }

  static async fetchDashboard(dashboardId) {
    if (!dashboardId) {
      return null;
    }

    try {
      const response = await apiClient.get(`/dashboard/${dashboardId}`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching dashboard:", error);
      return null;
    }
  }

  static async fetchDashboards(orgID) {
    if (!orgID) {
      return [];
    }

    try {
      const response = await apiClient.get(`/dashboard/organisation/${orgID}`);
      return response.data || [];
    } catch (error) {
      logger.error("Error fetching dashboards:", error);
      return [];
    }
  }

  static async updateWidget(widgetId, dashboardId, data) {
    if (!widgetId || !dashboardId || !data) {
      return false;
    }

    let response = await apiClient.put(
      `/dashboard/${dashboardId}/widget/${widgetId}`,
      data
    );

    //check if response is ok
    if (response.status === 200) {
      return true;
    } else {
      logger.error("Error updating widget:", response);
      return false;
    }
  }

  static async addWidget(widget, fields, dashboardId, dashboardWidgets) {
    try {
      if (!widget || !fields || !dashboardId) {
        return false;
      }

      //we need to work out the x and y position of the widget, we can do this by looking at the other widgets on the dashboard and finding the first available space
      //we can then use this space to place the widget
      //we can also set the width and height of the widget to be 2x2

      let x = 0;
      let y = 0;
      let w = 1;
      let h = 4;

      //if we have a map widget, set the w and height to 4x
      if (widget === 3) {
        w = 8;
        h = 16;
      } else if (widget === 2) {
        w = 4;
        h = 6;
      }

      //work out the order of the widget
      let order = dashboardWidgets.length + 1;

      //loop through dashboard widgets and find the highest x, and add 1 to it
      dashboardWidgets.forEach((widget) => {
        if (widget.y > y) {
          y = widget.y;
        }
      });
      y = y + 4;

      //we want to store fields object as json string with escaped quotes and commas, encode the object
      let safeFields = JSON.stringify(fields);

      let data = {
        dashboard_id: dashboardId,
        widget_id: widget,
        device_id: 1,
        field: safeFields,
        order: order,
        x: x,
        y: y,
        w: w,
        h: h,
      };

      await apiClient.post(`/dashboard/${dashboardId}/widget`, data);
      return true;
    } catch (error) {
      logger.warn(error);
      return false;
    }
  }

  static async deleteWidget(widgetId, dashboardId) {
    if (!widgetId || !dashboardId) {
      return false;
    }
    const response = await apiClient.delete(
      `/dashboard/${dashboardId}/widget/${widgetId}`
    );

    if (response.status === 200) {
      return true;
    }

    return false;
  }

  static async editDashboard(dashboardId, data) {
    let result = await apiClient.put(`/dashboard/${dashboardId}`, data);

    if (result) {
      return true;
    } else {
      return false;
    }
  }

  static async fetchDashboardDevices(dashboardID) {
    if (!dashboardID) {
      return [];
    }

    const response = await apiClient.get(`/dashboard/${dashboardID}/devices`);
    return response.data;
  }

  static async shareDashboard(dashboardId) {
    const response = await apiClient.post(`/dashboard/${dashboardId}/share`);
    return response.data;
  }
}
